import {Box, Group, Table} from '@mantine/core'
import {useLayoutEffect, useRef, useState} from 'react'
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'

import type {ProspectDistributionData} from 'src/api/client/models/analytics'
import {useCapacityBucketVerticalBarShape} from 'src/components/analytics/capacity-bucket-vertical-bar-shape'
import type {ProspectClickFilters} from 'src/components/analytics/use-prospect-navigation'
import type {ExportKind} from 'src/components/export-image-button'
import {SectionCard} from 'src/components/section-card'
import {CARTESIAN_GRID_PROPS, CHART_COLORS} from 'src/lib/chart-colors'
import {formatNumber} from 'src/lib/format'

const CHART_HEIGHT = 320
const CHART_MARGIN = {top: 24, right: 8, left: 8, bottom: 60}

type Props = {
    data: ProspectDistributionData
    onProspectFilter?: (filters: ProspectClickFilters) => void
}

type ChartTableLayout = 'stack' | 'sideBySide'
type ChartTableContent = 'chart' | 'table' | 'both'

function ProspectDistributionChartAndTable({
    data,
    onProspectFilter,
    layout = 'stack',
    content = 'both',
}: {
    data: ProspectDistributionData
    onProspectFilter?: (filters: ProspectClickFilters) => void
    layout?: ChartTableLayout
    content?: ChartTableContent
}) {
    const sortedRows = [...data.rows]
    const maxProspectCount = Math.max(0, ...sortedRows.map((r) => r.prospectCount))
    /** Sqrt scale includes 0. When every bucket is 0, avoid a degenerate [0,0] domain. */
    const prospectCountYDomain: [number, number | string] =
        maxProspectCount > 0 ? [0, 'dataMax'] : [0, 1]
    const chartColumnRef = useRef<HTMLDivElement>(null)
    const [sideBySideChartHeight, setSideBySideChartHeight] = useState(CHART_HEIGHT)

    useLayoutEffect(() => {
        if (layout !== 'sideBySide' || content !== 'both') {
            return
        }
        const el = chartColumnRef.current
        if (!el) {
            return
        }
        const updateHeight = () => {
            const h = el.getBoundingClientRect().height
            if (h > 0) {
                setSideBySideChartHeight(Math.floor(h))
            }
        }
        updateHeight()
        const observer = new ResizeObserver(updateHeight)
        observer.observe(el)
        return () => observer.disconnect()
    }, [layout, content, sortedRows.length, data.total])

    const chartPixelHeight =
        layout === 'sideBySide' && content === 'both' ? sideBySideChartHeight : CHART_HEIGHT

    const renderBarShape = useCapacityBucketVerticalBarShape(onProspectFilter, {
        chartHeight: chartPixelHeight,
        marginTop: CHART_MARGIN.top,
        marginBottom: CHART_MARGIN.bottom,
    })

    const chart = (
        <ResponsiveContainer width="100%" height={chartPixelHeight}>
            <BarChart data={sortedRows} margin={CHART_MARGIN}>
                <CartesianGrid {...CARTESIAN_GRID_PROPS} />
                <XAxis
                    dataKey="capacityBucket"
                    angle={-35}
                    textAnchor="end"
                    height={80}
                    interval={0}
                />
                <YAxis
                    type="number"
                    scale="sqrt"
                    domain={prospectCountYDomain}
                    allowDecimals={false}
                    tickFormatter={(v: number) => formatNumber(v)}
                />
                <Tooltip
                    formatter={(value: number) => [formatNumber(value), 'Count']}
                    labelFormatter={(label) => label}
                />
                <Bar
                    dataKey="prospectCount"
                    fill={CHART_COLORS.secondary}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                    shape={renderBarShape}
                >
                    <LabelList
                        dataKey="prospectCount"
                        position="top"
                        formatter={(v: number | string) => formatNumber(Number(v))}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    )

    const table = (
        <Box maw={layout === 'stack' ? 600 : undefined} w="100%">
            <Table striped highlightOnHover layout="fixed" mt={layout === 'stack' ? 'md' : 0}>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Capacity Bucket</Table.Th>
                        <Table.Th style={{textAlign: 'right'}}>Count</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {sortedRows.map((row) => {
                        const handleClick = onProspectFilter
                            ? () => onProspectFilter({capacityBucket: row.capacityBucket})
                            : undefined
                        return (
                            <Table.Tr key={row.capacityBucket}>
                                <Table.Td
                                    onClick={handleClick}
                                    style={handleClick ? {cursor: 'pointer'} : undefined}
                                >
                                    {row.capacityBucket}
                                </Table.Td>
                                <Table.Td
                                    onClick={handleClick}
                                    style={
                                        handleClick
                                            ? {textAlign: 'right', cursor: 'pointer'}
                                            : {textAlign: 'right'}
                                    }
                                >
                                    {formatNumber(row.prospectCount)}
                                </Table.Td>
                            </Table.Tr>
                        )
                    })}
                </Table.Tbody>
                <Table.Tfoot>
                    <Table.Tr>
                        <Table.Td>
                            <strong>Total</strong>
                        </Table.Td>
                        <Table.Td style={{textAlign: 'right'}}>
                            <strong>{formatNumber(data.total)}</strong>
                        </Table.Td>
                    </Table.Tr>
                </Table.Tfoot>
            </Table>
        </Box>
    )

    if (content === 'chart') {
        return chart
    }
    if (content === 'table') {
        return table
    }

    if (layout === 'sideBySide') {
        return (
            <Group align="stretch" gap="lg" wrap="nowrap">
                <Box
                    ref={chartColumnRef}
                    style={{
                        flex: '5 1 0',
                        minWidth: 0,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box style={{flex: 1, minHeight: 0, width: '100%'}}>{chart}</Box>
                </Box>
                <Box style={{flex: '2 1 0', minWidth: 0, overflow: 'auto'}}>{table}</Box>
            </Group>
        )
    }

    return (
        <>
            {chart}
            {table}
        </>
    )
}

/** Renders only the chart or only the table, used for PNG export. */
export function ProspectDistributionExportContent({
    data,
    kind,
}: {
    data: ProspectDistributionData
    kind: ExportKind
}) {
    if (kind === 'table') {
        return <ProspectDistributionChartAndTable data={data} layout="stack" content="table" />
    }
    return <ProspectDistributionChartAndTable data={data} layout="stack" content="chart" />
}

export default function ProspectDistribution({data, onProspectFilter}: Props) {
    return (
        <SectionCard title="Prospects by Capacity">
            <ProspectDistributionChartAndTable data={data} onProspectFilter={onProspectFilter} />
        </SectionCard>
    )
}
